import {
  MATCH_COUNTS_FIELD,
  RowOrigin,
  SourceKey,
} from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * Maps a Flex Positions read-API row onto the internal {@link WorkbenchRow}, mirroring Flex
 * Transactions' row.adapter.ts.
 *
 * Confirmed against real sample responses for the `fields: null` ("everything") request, the two
 * sources return different shapes for the same field:
 *  - Prometheus (Custody/Trading) rows are flat — every business field is a plain scalar directly
 *    on the row — and dump their unmodeled fields into a single `aggregateInformation` string
 *    (the target object's contents with the outer braces stripped, per aggregate-info.service.ts).
 *  - Enfusion rows wrap every business field in an envelope object: `{ value }` for a plain scalar,
 *    or `{ value, currency, quotationFormat, quoteType, source }` for a priced one. An envelope
 *    with no `value` key at all (e.g. `{}`) carries nothing for that row. Left un-unwrapped, these
 *    render as `[object Object]` — `fields` unwraps every value through the same rule regardless of
 *    source, since a Prometheus scalar already fails the "is this an envelope" test and passes
 *    through untouched.
 *
 * `matchStatus` and `matchCounts` are kept as `serverMatchStatus`/`serverMatchCounts`, a fallback
 * `matching.service.ts` reads only until a manual Match action gives it something more current.
 * `matchedIds` is kept as `serverMatchedIds` — real counterpart id lists, which is what names the
 * other side of an Unmatch request when no local match graph exists.
 *
 * KNOWN GAP — the position-specific promoted properties `marketValue`, `currency`, `sec`,
 * `positionType` and Enfusion's `sourceLabel` have no confirmed 1:1 field yet (Prometheus's
 * `marketValue` is a bare string with its currency, if any, buried inside `aggregateInformation`;
 * Enfusion's analogues use different key names entirely) — left undefined rather than guessed. The
 * ColDef factory still renders every catalog field correctly via `fields`; only these *promoted*
 * shortcuts (the Historical Balance defaults and the per-currency footer) are unpopulated until
 * those keys are confirmed.
 */

/** Status strings the API may use for `matchStatus`, normalised case-insensitively. */
const IGNORED_STATUS = new Set(['ignored', 'ignore'])

const text = (value: unknown): string | undefined =>
  value == null ? undefined : String(value)

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * The complete set of keys Enfusion's field envelope uses: `{ value }` for a plain scalar, or
 * `{ value, currency, quotationFormat, quoteType, source }` for a priced one — confirmed against
 * real sample responses (see the file header). An object whose keys are not a subset of this set
 * is not an envelope at all, so it is left for `formatCell` to render as JSON rather than treated
 * as a valueless envelope and blanked (a plain `{}` still trivially qualifies, and still means
 * "carries nothing" — the empty-envelope case below).
 */
const ENVELOPE_KEYS = new Set([
  'value',
  'currency',
  'quotationFormat',
  'quoteType',
  'source',
])

const looksLikeEnvelope = (value: Record<string, unknown>): boolean =>
  Object.keys(value).every(key => ENVELOPE_KEYS.has(key))

/**
 * Unwrap an Enfusion field envelope: `{ value }` becomes the scalar; `{ value, currency }` keeps its
 * currency (as a {@link Money}-shaped object) so the money formatter and per-currency footer still
 * pick it up at runtime (Decision 5) regardless of what the catalog calls the cell type; an envelope
 * with no `value` key carries nothing. A value that is not an envelope — a Prometheus scalar, or an
 * object with keys outside the envelope's own — passes through unchanged.
 */
const unwrapEnvelope = (value: unknown): unknown => {
  if (!isPlainObject(value)) return value
  if (!looksLikeEnvelope(value)) return value
  if (!('value' in value)) return null
  const inner = value['value']
  // Enfusion sends the literal string "null" for some absent optional fields, not JSON null.
  if (inner === 'null') return null
  return typeof inner === 'number' && typeof value['currency'] === 'string'
    ? { value: inner, currency: value['currency'] }
    : inner
}

/** Prometheus's unmodeled-field blob arrives as the target object's contents with the outer braces
 *  stripped, so it has to be re-wrapped before parsing. */
function parseAggregateInfo(value: unknown): Record<string, unknown> {
  if (value == null) return {}
  if (isPlainObject(value)) return value
  const raw = String(value).trim()
  if (!raw) return {}
  const wrapped = raw.startsWith('{') ? raw : `{${raw}}`
  try {
    const parsed = JSON.parse(wrapped)
    return isPlainObject(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * The API's per-target match counts, coerced to numbers.
 *
 * Read from TWO places, because the response shape depends on whether the request projected fields:
 *  - the whole `matchCounts` blob, when `fields` was omitted;
 *  - HOISTED top-level keys (`enfusionMatches`, `custodyMatches`, ...), when the request named them —
 *    the contract hoists a named nested key to the top level, so with the projection the blob is not
 *    sent at all and reading only it would report every count as zero.
 *
 * Blob keys win over hoisted ones where both appear, since the blob is the authoritative container.
 * An ABSENT count means 0, not null — the backend returns 0 explicitly, and a missing key after
 * null-omission means the same thing.
 */
function parseMatchCounts(
  blob: unknown,
  row: Record<string, unknown>,
): Record<string, number> {
  const counts: Record<string, number> = {}

  const absorb = (key: string, value: unknown): void => {
    const numeric = Number(value)
    if (!isNaN(numeric)) counts[key] = numeric
  }

  Object.values(MATCH_COUNTS_FIELD).forEach(field => {
    if (field in row) absorb(field, row[field])
  })

  if (isPlainObject(blob)) {
    Object.entries(blob).forEach(([key, count]) => absorb(key, count))
  }

  return counts
}

/** The API's per-target matched-row id blob (e.g. `{ enfusionMatches: ['e1','e2'] }`).
 *
 *  These are the real counterpart identities — MatchHydrationService writes `matched_ids` under the
 *  same keys as `match_counts` — so they are promoted to a typed property rather than left as an
 *  untyped `fields` key for the unmatch payload to dig out. Anything that is not an array of
 *  scalars for a key is dropped rather than coerced: a key that does not carry ids carries nothing.
 */
function parseMatchedIds(value: unknown): Record<string, string[]> {
  if (!isPlainObject(value)) return {}
  const matched: Record<string, string[]> = {}
  Object.entries(value).forEach(([key, ids]) => {
    if (!Array.isArray(ids)) return
    matched[key] = ids.filter(id => id != null).map(id => String(id))
  })
  return matched
}

/**
 * The API's per-row action eligibility (`availableActions`, PVAL-26401), kept as raw tokens — see
 * {@link WorkbenchRow.availableActions} for why normalising here would be wrong.
 *
 * Anything that is not an array (absent, null, or a malformed scalar) becomes `undefined`, i.e. "the
 * backend expressed no restriction", which is the same state as a pre-PVAL-26401 response and leaves
 * every item enabled subject to the local gates. A present-but-empty array is preserved as `[]`,
 * which means the same thing — the distinction is kept only so the two cases stay legible in a
 * response dump. Blank entries are dropped so `['', 'MATCH']` cannot be mistaken for two tokens.
 */
function parseAvailableActions(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return value
    .filter(entry => entry != null)
    .map(entry => String(entry).trim())
    .filter(entry => entry.length > 0)
}

/** Prometheus's `dataSource` field names the sub-feed a row came from. */
const DATA_SOURCE_ORIGIN: Record<string, RowOrigin> = {
  CUSTODY: 'cust',
  TRADING: 'trad',
}

/**
 * Prometheus rows are either Custody or Trading, which drives the Source column and match
 * counting. An unrecognised or missing `dataSource` falls back to Custody, matching Flex
 * Transactions' precedent for an unrecognised value.
 */
const originOf = (
  sourceKey: SourceKey,
  row: Record<string, unknown>,
): RowOrigin => {
  if (sourceKey === 'api') return 'api'
  if (sourceKey === 'cw') return 'cw'
  return (
    DATA_SOURCE_ORIGIN[String(row['dataSource'] ?? '').toUpperCase()] ?? 'cust'
  )
}

export function rowFromApi(
  sourceKey: SourceKey,
  row: Record<string, unknown>,
  index: number,
): WorkbenchRow {
  // Three keys are destructured OUT so they cannot leak into `fields` as would-be display columns,
  // each because it is promoted to a typed property instead: `matchCounts` (-> serverMatchCounts),
  // `matchedIds` (-> serverMatchedIds) and `availableActions` (-> the context menu's enablement;
  // see {@link WorkbenchRow.availableActions}).
  //
  // `aggregateInformation` is deliberately NOT one of them: it is read directly off `row` so it is
  // BOTH parsed into `aggregateInfo` below AND left in `fields` under its own key — the catalog
  // lists "Aggregate Information" as an ordinary displayable POS_DATA column, and excluding it from
  // `fields` left that column permanently blank regardless of what the backend sent.
  const { matchCounts, matchedIds, availableActions, ...restOfRow } = row
  const aggregateInformation = row['aggregateInformation']
  const fields: Record<string, unknown> = {}
  Object.entries(restOfRow).forEach(([key, value]) => {
    fields[key] = unwrapEnvelope(value)
  })

  // Fall back to the page index so a row missing its identity field still renders and stays
  // addressable, rather than colliding on an empty id with every other such row.
  const rowId = text(row['rowId']) ?? `${sourceKey}:${index}`
  const status = String(row['matchStatus'] ?? '').toLowerCase()
  const origin = originOf(sourceKey, row)
  const units = row['units']

  return {
    rowId,
    origin,
    // No group is seeded from the response. `matchedIds` DOES carry the matched rows' real
    // identities (see serverMatchedIds below), so a real cross-source link could be built from it —
    // but seeding `group` here would make every server-matched row read as locally matched, which
    // is exactly what the wired Match path must never do (statusOf returns 'matched' for any row
    // with a group, and no re-read can undo it). `group` stays local to the matching engine's own
    // Match action; the server's matchStatus/matchCounts/matchedIds are the fallback Status, the
    // match-count columns and the unmatch payload's counterpart list respectively.
    group: undefined,
    ignored: IGNORED_STATUS.has(status),
    accountId: text(row['accountId']) ?? '',
    // Only Prometheus carries this — the calc unit-difference columns sum matched Prometheus units.
    units:
      (origin === 'cust' || origin === 'trad') && typeof units === 'number'
        ? units
        : undefined,
    aggregateInfo: parseAggregateInfo(aggregateInformation),
    serverMatchStatus: text(row['matchStatus'])?.toUpperCase(),
    // `row` as well as the blob: with a projected request the counts arrive HOISTED to the top
    // level instead of nested (see parseMatchCounts).
    serverMatchCounts: parseMatchCounts(matchCounts, row),
    serverMatchedIds: parseMatchedIds(matchedIds),
    availableActions: parseAvailableActions(availableActions),
    fields,
  }
}

/** Map a whole page of read-API rows for one source. */
export function rowsFromApi(
  sourceKey: SourceKey,
  data: Array<Record<string, unknown>>,
): WorkbenchRow[] {
  return data.map((row, index) => rowFromApi(sourceKey, row, index))
}
